import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { useAppSelector } from '../hooks/reduxHooks';
import { blueTheme, createCustomTheme, greenTheme } from '../theme/theme';
import { useRoles } from '../hooks/useRoles';
import { RootState } from '../redux/store';
import { APP_TAB_TITLE, COLORS_GENERIC } from '../services/consts';

interface ThemeContextProps {
    theme: any;
    setTheme: (theme: any) => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const useThemeContext = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useThemeContext must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
    const [theme, setTheme] = useState(greenTheme);
    const userData = useAppSelector((state: RootState) => state.auth.user);
    const { isAdminBO, isSupervisorSIMP, isOperatorSIMP, isViewerBO, isRRHHSIMP, isPreventor } =
        useRoles();

    const updateCssVariables = (theme: any) => {
        document.documentElement.style.setProperty('--primary', theme.palette.primary.main);
    };

    useEffect(() => {
        const storedTheme = localStorage.getItem('theme');
        if (storedTheme === 'blue') {
            setTheme(greenTheme);
            document.title = APP_TAB_TITLE.BO;
        } else {
            setTheme(greenTheme);
            document.title = APP_TAB_TITLE.SIMP;
        }
    }, []);

    useEffect(() => {
        
        if (isSupervisorSIMP || isOperatorSIMP || isViewerBO || isRRHHSIMP) {
            setTheme(greenTheme);
            document.title = APP_TAB_TITLE.SIMP;
        } else if (isAdminBO || isPreventor) {
            setTheme(blueTheme);
            document.title = APP_TAB_TITLE.BO;
        }

        const defaultColors = {
            primaryColor: COLORS_GENERIC.primaryColor,
            primaryLight: COLORS_GENERIC.primaryLight
        }

        const userDataColors = {
            primaryColor: userData?.organizacion?.colorPrimario || defaultColors.primaryColor,
            primaryLight: userData?.organizacion?.colorSecundario || defaultColors.primaryLight
        };

        setTheme(createCustomTheme(userDataColors.primaryColor, userDataColors.primaryLight));
    }, [userData]);

    useEffect(() => {
        updateCssVariables(theme);
    }, [theme]);

    return (
        <ThemeContext.Provider value={{ theme, setTheme }}>
            <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
        </ThemeContext.Provider>
    );
};
